/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Use Snowflake Composables
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import type { UseFetchOptions } from '#app'
import { v4 as uuidv4 } from 'uuid'
import { snowflakeParseResponse } from '~/scripts/snowflake/snowflakeResponseParser'
import type { SnowflakeAuthentication, SnowflakeOptions, SnowflakeResponse } from '~/types/typesSnowflake'

async function snowflakeQueryExecution({ query, queryId, returnType = 'json', async, polling, executor = 'user', customConnection }: SnowflakeOptions, fetchOptions?: UseFetchOptions<any>) {
  try {
    // Get Auth Token and Account Credentials
    // Get Token and Snowflake ID
    const { snowflakeAuth } = useSnowflakeAuthStore()
    const { accountNamespace } = storeToRefs(useGlobalsStore())

    // Swap Snowflake Snippets
    query = query?.replace('{db}', accountNamespace.value || '')

    const snowflakeCredentials = await snowflakeAuth(executor, customConnection)
    if (!snowflakeCredentials.accessToken || !snowflakeCredentials.sfId || !snowflakeCredentials.authType) throw new Error('Missing Snowflake Access Token, SFID, or AuthType')
    const fetch = snowflakeFetch({ query, queryId, executor }, snowflakeCredentials, fetchOptions)
    const url = queryId ? `/api/v2/statements/${queryId}` : '/api/v2/statements'
    const response: any = await fetch(url)

    console.log('Snowflake Query: ', query)
    console.log('Snowflake Response: ', response)

    // If Multiple Statements Where Sent - Get Results From Latest Statement Handle by Executing Again with Query ID
    if (response.statementHandles && response.statementHandles.length > 1) {
      const lastQueryId: string = response.statementHandles[response.statementHandles.length - 1]

      // const getLatestResults = snowflakeFetch({ query: response.statementHandles[response.statementHandles.length - 1], executor }, snowflakeCredentials, fetchOptions)
      return await snowflakeQueryExecution({ queryId: lastQueryId, returnType, async, polling, executor, customConnection }, fetchOptions)
    }

    // Parse Response
    return snowflakeParseResponse(response, returnType)
  } catch (error: any) {
    const errorMessage = error.message ? error.message : error

    console.error(`Snowflake Error: ${errorMessage}`)

    return { status: 'ERROR', error: errorMessage } as SnowflakeResponse
  }
}

// //////////////////////////////////////////////////////////////////
// Main Execution Function
// //////////////////////////////////////////////////////////////////
export default async function ({ query, queryId, returnType = 'json', async, polling, executor = 'user', customConnection }: SnowflakeOptions, fetchOptions?: UseFetchOptions<any>) {
  if (!query && !queryId) throw new Error('Missing Query or Query ID')

  if (!async && !polling) {
    // //////////////////////////////////////////////////////////////////
    // Regular $fetch Execution
    // //////////////////////////////////////////////////////////////////
    return await snowflakeQueryExecution({ query, queryId, returnType, async, polling, executor, customConnection }, fetchOptions)
  } else {
    return { status: 'ERROR', error: 'Async and Polling Not Yet Implemented' } as SnowflakeResponse
  }

  // if (async) {
  //   // //////////////////////////////////////////////////////////////////
  //   // Async "useFetch" Snowflake Execution
  //   // //////////////////////////////////////////////////////////////////
  //   console.log('Using useFetch')
  //   // return useFetch(url, {
  //   //   ...options,
  //   //   onResponseError({ response }) {
  //   //     responseErrorParser(response)
  //   //   },
  //   //   transform: (data) => {
  //   //     return data
  //   //   },
  //   // })
  // }
}

// //////////////////////////////////////////////////////////////////
// Create .fetch Parameters
// //////////////////////////////////////////////////////////////////
function snowflakeFetch({ query, queryId, executor }: SnowflakeOptions, snowflakeAuthentication: SnowflakeAuthentication, fetchOptions?: UseFetchOptions<any>) {
  // Get Snowflake Namespace
  const { accountNamespace } = storeToRefs(useGlobalsStore())

  // Snowflake Authentication
  const { sfId, accessToken } = snowflakeAuthentication

  // The Following is Used for RSA - Uncomment the Following
  // const { sfId, token } = snowflakeAuth(executor, customConnection)
  // Params
  const params = {
    requestId: uuidv4(),
    async: false,
    nullable: true,
    includePresignedUrl: true,
    default_db: null as string | null,
    default_schema: null as string | null,
  }

  // Add Snowflake Namespace if Available
  if (accountNamespace.value) {
    params.default_db = accountNamespace.value.split('.')[0] || null
    params.default_schema = accountNamespace.value.split('.')[1] || null
  }

  console.log('Snowflake Parameters', params)

  return $fetch.create({
    baseURL: `https://${sfId}.snowflakecomputing.com`,
    onRequest({ options }) {
      // args: request, error
      options.method = queryId ? 'GET' : 'POST'
      options.headers.set('User-Agent', 'SnowFlow')
      options.headers.set('Content-Type', 'application/json')
      options.headers.set('Accept', 'application/json')

      // options.headers.set('X-Snowflake-Authorization-Token-Type', 'KEYPAIR_JWT')
      options.headers.set('Authorization', `Bearer ${accessToken}`)
      options.params = params
      if (query) options.body = { statement: query, parameters: { MULTI_STATEMENT_COUNT: 0 } }
    },
    onResponseError({ response }) {
      const error = response._data.message

      console.error(error)
      throw new Error(error)
    },
  })
}
